import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Box,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  collection,
  collectionGroup,
  doc,
  getDocs,
  getDoc,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
} from 'firebase/firestore';
import { useAuth } from '../AuthContext';
import {
  DEFAULT_OFFICE_LOAN_TEMPLATE_ID,
  DEFAULT_OFFICE_MAIN_TEMPLATE_ID,
  defaultOfficeLoanTemplate,
  defaultOfficeMainTemplate,
} from '../office/workflows';
import { generateUniqueBookingShortCode } from '../office/documentRefs';
import { compareUnitNumbers } from '../utils/unitSort';
import { computeGstAmountForUnit } from '../office/gst';
import { useNavigate } from 'react-router-dom';

type UnitBookingStatus = 'available' | 'blocked' | 'booked' | 'sold';

type UnitRow = {
  id: string;
  unitNumber?: string | null;
  unitNumberLower?: string | null;
  blockType?: string | null;
  usage?: string | null;
  unitAmount?: number | null;
  loanToDeveloperAmount?: number | null;
  carpetArea?: number | null;
  exclusiveBalconyArea?: number | null;
  bookingStatus?: UnitBookingStatus | null;
  activeBookingId?: string | null;
  hasBookingFormDate?: boolean | null;
  latestBookingFormDate?: string | null;
  isActive?: boolean | null;
};

type UnitFilterPreset =
  | 'payment-pending'
  | 'fully-received'
  | 'has-loan'
  | 'loan-pending'
  | 'gst-pending'
  | 'gst-received';

type VisibleUnitColumns = {
  customer: boolean;
  amount: boolean;
  received: boolean;
  pending: boolean;
  loan: boolean;
  gst: boolean;
  status: boolean;
  bank: boolean;
};

const UNIT_COLUMNS_STORAGE_KEY = 'office-units-visible-columns';
const DEFAULT_VISIBLE_UNIT_COLUMNS: VisibleUnitColumns = {
  customer: true,
  amount: true,
  received: false,
  pending: false,
  loan: false,
  gst: false,
  status: false,
  bank: false,
};

const UNIT_COLUMN_OPTIONS: Array<{ key: keyof VisibleUnitColumns; label: string }> = [
  { key: 'customer', label: 'Customer' },
  { key: 'amount', label: 'Amount' },
  { key: 'received', label: 'Received' },
  { key: 'pending', label: 'Pending' },
  { key: 'loan', label: 'Loan amount' },
  { key: 'gst', label: 'GST status' },
  { key: 'status', label: 'Status' },
  { key: 'bank', label: 'Loan bank' },
];

type BankTransactionRow = {
  unitId?: string | null;
  unitNumber?: string | null;
  txnType?: string | null;
  receiptCategory?: string | null;
  paymentCategory?: string | null;
  isLoanDisbursement?: boolean | null;
  amount?: number | null;
};

const UNIT_FILTER_OPTIONS: Array<{ key: UnitFilterPreset; label: string }> = [
  { key: 'payment-pending', label: 'Payment pending' },
  { key: 'fully-received', label: 'Fully received' },
  { key: 'has-loan', label: 'Has loan' },
  { key: 'loan-pending', label: 'Loan pending' },
  { key: 'gst-pending', label: 'GST pending' },
  { key: 'gst-received', label: 'GST received' },
];

const OPPOSITE_UNIT_FILTERS: Partial<Record<UnitFilterPreset, UnitFilterPreset[]>> = {
  'payment-pending': ['fully-received'],
  'fully-received': ['payment-pending', 'loan-pending'],
  'loan-pending': ['fully-received'],
  'gst-pending': ['gst-received'],
  'gst-received': ['gst-pending'],
};

type BookingCustomerRow = {
  displayName?: string | null;
  name?: string | null;
  firstName?: string | null;
  middleName?: string | null;
  surname?: string | null;
  role?: string | null;
};

const statusLabel: Record<UnitBookingStatus, string> = {
  available: 'Available',
  blocked: 'Blocked',
  booked: 'Booked',
  sold: 'Sold',
};

const statusColor: Record<UnitBookingStatus, any> = {
  available: 'default',
  blocked: 'warning',
  booked: 'success',
  sold: 'secondary',
};

export default function OfficeUnitsPage() {
  const { currentUser, currentProjectId } = useAuth();
  const db = useMemo(() => getFirestore(), []);
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [units, setUnits] = useState<UnitRow[]>([]);
  const [transactions, setTransactions] = useState<BankTransactionRow[]>([]);
  const [projectMeta, setProjectMeta] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [filter, setFilter] = useState<UnitBookingStatus | 'all' | 'form'>('booked');
  const [presetFilters, setPresetFilters] = useState<Set<UnitFilterPreset>>(new Set());
  const [columnsAnchor, setColumnsAnchor] = useState<HTMLElement | null>(null);
  const [visibleColumns, setVisibleColumns] = useState<VisibleUnitColumns>(() => {
    if (typeof window === 'undefined') return DEFAULT_VISIBLE_UNIT_COLUMNS;
    try {
      const stored = JSON.parse(window.localStorage.getItem(UNIT_COLUMNS_STORAGE_KEY) || '{}');
      return {
        customer: stored.customer !== false,
        amount: stored.amount !== false,
        received: stored.received === true,
        pending: stored.pending === true,
        loan: stored.loan === true,
        gst: stored.gst === true,
        status: stored.status === true,
        bank: stored.bank === true,
      };
    } catch {
      return DEFAULT_VISIBLE_UNIT_COLUMNS;
    }
  });
  const [search, setSearch] = useState('');
  const [unitBookingKeys, setUnitBookingKeys] = useState<Set<string>>(new Set());
  const [unitBookingSortTsByUnitId, setUnitBookingSortTsByUnitId] = useState<Map<string, number>>(new Map());
  const [primaryCustomerByUnitId, setPrimaryCustomerByUnitId] = useState<Map<string, string>>(new Map());
  const [sanctionBankByUnitId, setSanctionBankByUnitId] = useState<Map<string, string>>(new Map());

  const [savingUnitId, setSavingUnitId] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [confirmAvailabilityUnit, setConfirmAvailabilityUnit] = useState<UnitRow | null>(null);

  useEffect(() => {
    setLoadError(null);
    setUnits([]);
    if (!currentProjectId) {
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    const unitsCol = collection(db, 'projects', currentProjectId, 'units');
    const q = query(unitsCol, orderBy('unitNumberLower', 'asc'), limit(5000));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as UnitRow[];
        // Filter out inactive units (soft-deleted by import).
        setUnits(
          rows
            .filter((r) => r.isActive !== false)
            .sort((a, b) => compareUnitNumbers(a.unitNumber || a.id, b.unitNumber || b.id))
        );
        setLoading(false);
      },
      (err) => {
        console.error('Load units failed', err);
        setLoadError(err?.message || 'Failed to load units.');
        setLoading(false);
      }
    );
    return () => unsub();
  }, [currentProjectId, db]);

  useEffect(() => {
    if (!currentProjectId) {
      setTransactions([]);
      setProjectMeta(null);
      return undefined;
    }
    const unsubscribeTransactions = onSnapshot(
      collection(db, 'projects', currentProjectId, 'bankTransactions'),
      (snap) => setTransactions(snap.docs.map((entry) => entry.data() as BankTransactionRow)),
      (err) => {
        console.error('Load unit payment filters failed', err);
        setTransactions([]);
      }
    );
    const unsubscribeProject = onSnapshot(
      doc(db, 'projects', currentProjectId),
      (snap) => setProjectMeta(snap.exists() ? (snap.data() as Record<string, any>) : null),
      () => setProjectMeta(null)
    );
    return () => {
      unsubscribeTransactions();
      unsubscribeProject();
    };
  }, [currentProjectId, db]);

  const unitsRef = useRef<UnitRow[]>([]);

  useEffect(() => {
    unitsRef.current = units;
  }, [units]);

  useEffect(() => {
    window.localStorage.setItem(UNIT_COLUMNS_STORAGE_KEY, JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  useEffect(() => {
    let cancelled = false;
    const loadPrimaryCustomerNames = async () => {
      if (!currentProjectId) {
        setPrimaryCustomerByUnitId(new Map());
        return;
      }
      try {
        const targets = units
          .map((unit) => ({
            unitId: String(unit.id || '').trim(),
            bookingId: String(unit.activeBookingId || '').trim(),
            directName: String(
              (unit as any)?.primaryCustomerName || (unit as any)?.customerName || (unit as any)?.allotteeName || ''
            ).trim(),
          }))
          .filter((row) => row.unitId.length > 0);
        const next = new Map<string, string>();
        await Promise.all(
          targets.map(async ({ unitId, bookingId, directName }) => {
            if (directName) {
              next.set(unitId, directName);
              return;
            }
            if (!bookingId) return;
            const customersSnap = await getDocs(
              collection(db, 'projects', currentProjectId, 'units', unitId, 'bookings', bookingId, 'customers')
            );
            let primaryName = '';
            let fallbackName = '';
            customersSnap.docs.forEach((d) => {
              const row = (d.data() || {}) as BookingCustomerRow;
              const resolvedName = String(
                row.displayName || row.name || [row.firstName, row.middleName, row.surname].filter(Boolean).join(' ')
              ).trim();
              if (!resolvedName) return;
              const role = String(row.role || '').trim().toLowerCase();
              if (role === 'primary' && !primaryName) primaryName = resolvedName;
              if (!fallbackName) fallbackName = resolvedName;
            });
            const best = primaryName || fallbackName;
            if (best) next.set(unitId, best);
          })
        );
        if (cancelled) return;
        setPrimaryCustomerByUnitId(next);
      } catch (err: any) {
        if (!cancelled) {
          console.error('Load primary customer names failed', err);
          setPrimaryCustomerByUnitId(new Map());
        }
      }
    };
    loadPrimaryCustomerNames();
    return () => {
      cancelled = true;
    };
  }, [currentProjectId, db, units]);

  useEffect(() => {
    let cancelled = false;
    const loadLoanSanctionBanks = async () => {
      if (!currentProjectId) {
        setSanctionBankByUnitId(new Map());
        return;
      }
      try {
        const targets = units
          .map((unit) => ({
            unitId: String(unit.id || '').trim(),
            bookingId: String(unit.activeBookingId || '').trim(),
          }))
          .filter((row) => row.unitId.length > 0 && row.bookingId.length > 0);
        if (!targets.length) {
          setSanctionBankByUnitId(new Map());
          return;
        }

        const readLoanCaseBankName = (caseData: any): string => {
          const stepData = ((caseData?.stepData || {}) as Record<string, any>) || {};
          const stepState = ((caseData?.stepState || {}) as Record<string, any>) || {};
          const candidates: Array<{ bank: string; sanctionDate: string; doneAtMs: number }> = [];
          Object.entries(stepData).forEach(([stepKey, row]) => {
            const item = (row || {}) as Record<string, any>;
            const bank = String(item?.sanctionBankName || '').trim();
            if (!bank) return;
            const sanctionDate = String(item?.sanctionDate || '').trim();
            const doneAtMs = Number(stepState?.[stepKey]?.doneAtMs || 0);
            candidates.push({ bank, sanctionDate, doneAtMs: Number.isFinite(doneAtMs) ? doneAtMs : 0 });
          });
          if (!candidates.length) return '';
          candidates.sort((a, b) => {
            if (a.sanctionDate !== b.sanctionDate) {
              return String(b.sanctionDate).localeCompare(String(a.sanctionDate));
            }
            return Number(b.doneAtMs || 0) - Number(a.doneAtMs || 0);
          });
          return String(candidates[0]?.bank || '').trim();
        };

        const next = new Map<string, string>();
        await Promise.all(
          targets.map(async ({ unitId, bookingId }) => {
            const caseIds = [`loan_${bookingId}`, bookingId];
            for (const caseId of caseIds) {
              const caseRef = doc(db, 'projects', currentProjectId, 'units', unitId, 'cases', caseId);
              const caseSnap = await getDoc(caseRef);
              if (!caseSnap.exists()) continue;
              const bankName = readLoanCaseBankName(caseSnap.data() || {});
              if (bankName) {
                next.set(unitId, bankName);
                break;
              }
            }
          })
        );
        if (cancelled) return;
        setSanctionBankByUnitId(next);
      } catch (err: any) {
        if (!cancelled) {
          console.error('Load sanction bank names failed', err);
          setSanctionBankByUnitId(new Map());
        }
      }
    };
    loadLoanSanctionBanks();
    return () => {
      cancelled = true;
    };
  }, [currentProjectId, db, units]);

  const normalizedSearch = useMemo(
    () => String(search || '').trim().toLowerCase().replace(/\s+/g, ' '),
    [search]
  );

  const indexedUnits = useMemo(
    () =>
      units.map((u) => ({
        ...u,
        _status: (u.bookingStatus || 'available') as UnitBookingStatus,
        _searchHay: [
          u.unitNumber,
          primaryCustomerByUnitId.get(String(u.id || '').trim()) || '',
          u.blockType ? `block ${u.blockType}` : '',
          u.usage,
          sanctionBankByUnitId.get(String(u.id || '').trim()) || '',
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase(),
      })),
    [primaryCustomerByUnitId, sanctionBankByUnitId, units]
  );

  const statusBuckets = useMemo(() => {
    const next: Record<UnitBookingStatus | 'all', typeof indexedUnits> = {
      all: indexedUnits,
      available: [],
      blocked: [],
      booked: [],
      sold: [],
    };
    indexedUnits.forEach((u) => {
      next[u._status].push(u);
    });
    return next;
  }, [indexedUnits]);

  useEffect(() => {
    if (!currentProjectId) {
      setUnitBookingKeys(new Set());
      return undefined;
    }
    const q = query(collectionGroup(db, 'bookings'));
    return onSnapshot(
      q,
      (snap) => {
        const next = new Set<string>();
        const nextSortTs = new Map<string, number>();
        const parseDateTime = (dateRaw: any, timeRaw: any): number | null => {
          const dateStr = String(dateRaw || '').trim();
          const timeStr = String(timeRaw || '').trim();
          if (!dateStr) return null;

          const normalized = dateStr.replace(/\./g, '-').replace(/\//g, '-');
          const parts = normalized.split('-').map((p) => String(p || '').trim());
          if (parts.length !== 3) return null;

          let year = 0;
          let month = 0;
          let day = 0;
          if (parts[0].length === 4) {
            year = Number(parts[0]);
            month = Number(parts[1]);
            day = Number(parts[2]);
          } else if (parts[2].length === 4) {
            day = Number(parts[0]);
            month = Number(parts[1]);
            year = Number(parts[2]);
          } else {
            return null;
          }
          if (!year || !month || !day) return null;

          let hour = 0;
          let minute = 0;
          if (timeStr) {
            const match = timeStr.match(/^(\d{1,2}):(\d{2})(?:\s*([AaPp][Mm]))?$/);
            if (match) {
              hour = Number(match[1] || 0);
              minute = Number(match[2] || 0);
              const meridiem = String(match[3] || '').toUpperCase();
              if (meridiem === 'PM' && hour < 12) hour += 12;
              if (meridiem === 'AM' && hour === 12) hour = 0;
            }
          }

          const dt = new Date(year, month - 1, day, hour, minute, 0, 0);
          const ts = dt.getTime();
          return Number.isFinite(ts) ? ts : null;
        };
        snap.docs.forEach((d) => {
          const parts = d.ref.path.split('/');
          const isUnitBookingPath =
            parts.length >= 6 &&
            parts[0] === 'projects' &&
            parts[2] === 'units' &&
            parts[4] === 'bookings';
          if (!isUnitBookingPath) return;
          const projectIdFromPath = String(parts[1] || '');
          const unitIdFromPath = String(parts[3] || '');
          if (projectIdFromPath !== currentProjectId || !unitIdFromPath) return;
          const key = String(unitIdFromPath || '').trim().toLowerCase();
          next.add(key);
          const row: any = d.data() || {};
          const createdAtMs = Number(
            (row?.createdAt as any)?.toMillis?.() || (row?.createdAt?.seconds ? row.createdAt.seconds * 1000 : 0) || 0
          );
          const parsed =
            parseDateTime(row?.bookingFormDate, row?.bookingFormTime) ??
            parseDateTime(row?.requestDate, row?.requestTime) ??
            createdAtMs;
          const prev = Number(nextSortTs.get(key) || 0);
          if (!prev || (parsed > 0 && parsed < prev)) nextSortTs.set(key, parsed);
        });
        setUnitBookingKeys(next);
        setUnitBookingSortTsByUnitId(nextSortTs);
      },
      (err) => {
        console.error('Load unit bookings for Form filter failed', err);
        const fallback = new Set<string>();
        unitsRef.current.forEach((u) => {
          if (String((u as any)?.activeBookingId || '').trim()) {
            fallback.add(String(u.id || '').trim().toLowerCase());
          }
        });
        setUnitBookingKeys(fallback);
        setUnitBookingSortTsByUnitId(new Map());
      }
    );
  }, [currentProjectId, db]);

  const paymentTotalsByUnitId = useMemo(() => {
    const unitIdByNumber = new Map(
      units
        .filter((unit) => String(unit.unitNumber || '').trim())
        .map((unit) => [String(unit.unitNumber || '').trim().toLowerCase(), unit.id])
    );
    const totals = new Map<string, { customer: number; loan: number; gst: number; refunds: number }>();
    const getTotals = (unitId: string) => {
      const existing = totals.get(unitId);
      if (existing) return existing;
      const next = { customer: 0, loan: 0, gst: 0, refunds: 0 };
      totals.set(unitId, next);
      return next;
    };
    transactions.forEach((transaction) => {
      const unitId = String(transaction.unitId || '').trim() || unitIdByNumber.get(String(transaction.unitNumber || '').trim().toLowerCase()) || '';
      if (!unitId) return;
      const amount = Math.max(0, Number(transaction.amount || 0) || 0);
      const totalsForUnit = getTotals(unitId);
      if (String(transaction.txnType || 'receipt').toLowerCase() === 'receipt') {
        const category = String(transaction.receiptCategory || '').toLowerCase();
        if (category === 'customer') {
          if (transaction.isLoanDisbursement === true) totalsForUnit.loan += amount;
          else totalsForUnit.customer += amount;
        }
        if (category === 'gst') totalsForUnit.gst += amount;
      }
      if (String(transaction.txnType || '').toLowerCase() === 'payment' && String(transaction.paymentCategory || '').toLowerCase() === 'customer_refund') {
        totalsForUnit.refunds += amount;
      }
    });
    return totals;
  }, [transactions, units]);

  const togglePresetFilter = (key: UnitFilterPreset) => {
    setPresetFilters((previous) => {
      const next = new Set(previous);
      if (next.has(key)) next.delete(key);
      else {
        OPPOSITE_UNIT_FILTERS[key]?.forEach((opposite) => next.delete(opposite));
        next.add(key);
      }
      return next;
    });
  };

  const filtered = useMemo(() => {
    const hasBookingDoc = (u: (typeof indexedUnits)[number]) => {
      const keys = [
        String(u.id || '').trim().toLowerCase(),
        String(u.unitNumber || '').trim().toLowerCase(),
        String(u.unitNumberLower || '').trim().toLowerCase(),
      ].filter(Boolean);
      return keys.some((k) => unitBookingKeys.has(k));
    };
    const base =
      filter === 'form'
        ? indexedUnits.filter(hasBookingDoc)
        : statusBuckets[filter];
    const searched = !normalizedSearch ? base : base.filter((u) => u._searchHay.includes(normalizedSearch));
    const presetFiltered = searched.filter((unit) => {
      if (!presetFilters.size) return true;
      const unitAmount = Math.max(0, Number(unit.unitAmount || 0) || 0);
      const loanAmount = Math.min(unitAmount, Math.max(0, Number(unit.loanToDeveloperAmount || 0) || 0));
      const totals = paymentTotalsByUnitId.get(unit.id) || { customer: 0, loan: 0, gst: 0, refunds: 0 };
      const receivedAmount = Math.max(0, totals.customer + totals.loan - totals.refunds);
      const gstAmount = computeGstAmountForUnit(unitAmount, unit as any, projectMeta as any, 1);
      return Array.from(presetFilters).every((preset) => {
        if (preset === 'payment-pending') return unitAmount > 0 && receivedAmount < unitAmount;
        if (preset === 'fully-received') return unitAmount > 0 && receivedAmount >= unitAmount;
        if (preset === 'has-loan') return loanAmount > 0;
        if (preset === 'loan-pending') return loanAmount > 0 && totals.loan < loanAmount;
        if (preset === 'gst-pending') return gstAmount > 0 && totals.gst < gstAmount;
        return gstAmount > 0 && totals.gst >= gstAmount;
      });
    });
    if (filter !== 'form') return presetFiltered;
    return [...presetFiltered].sort((a, b) => {
      const aKey = String(a.id || '').trim().toLowerCase();
      const bKey = String(b.id || '').trim().toLowerCase();
      const aTs = Number(unitBookingSortTsByUnitId.get(aKey) || Number.MAX_SAFE_INTEGER);
      const bTs = Number(unitBookingSortTsByUnitId.get(bKey) || Number.MAX_SAFE_INTEGER);
      if (aTs !== bTs) return aTs - bTs;
      return compareUnitNumbers(a.unitNumber || a.id, b.unitNumber || b.id);
    });
  }, [filter, indexedUnits, normalizedSearch, paymentTotalsByUnitId, presetFilters, projectMeta, statusBuckets, unitBookingKeys, unitBookingSortTsByUnitId]);

  const formUnitsCount = useMemo(
    () =>
      indexedUnits.filter((u) => {
        const keys = [
          String(u.id || '').trim().toLowerCase(),
          String(u.unitNumber || '').trim().toLowerCase(),
          String(u.unitNumberLower || '').trim().toLowerCase(),
        ].filter(Boolean);
        return keys.some((k) => unitBookingKeys.has(k));
      }).length,
    [indexedUnits, unitBookingKeys]
  );

  const counts = useMemo(() => {
    return {
      available: statusBuckets.available.length,
      blocked: statusBuckets.blocked.length,
      booked: statusBuckets.booked.length,
      sold: statusBuckets.sold.length,
    } as Record<UnitBookingStatus, number>;
  }, [statusBuckets]);

  const updateUnitStatus = async (unit: UnitRow, next: UnitBookingStatus, meta?: any): Promise<string | null> => {
    if (!currentProjectId) throw new Error('Select project first.');
    if (!currentUser?.email) throw new Error('Not signed in.');
    setSavingUnitId(unit.id);
    try {
      const unitRef = doc(db, 'projects', currentProjectId, 'units', unit.id);
      const unitSnap = await getDoc(unitRef);
      const latestUnitData = (unitSnap.data() as any) || {};
      let seededBookingId = '';
      let seededShortBookingCode = '';
      if (next !== 'available' && !String(latestUnitData.activeBookingId || unit.activeBookingId || '').trim()) {
        seededBookingId = doc(collection(unitRef, 'bookings')).id;
        seededShortBookingCode = await generateUniqueBookingShortCode({
          db,
          projectId: currentProjectId,
        });
      }
      const bookingIdOut = await runTransaction(db, async (tx) => {
        const projectRef = doc(db, 'projects', currentProjectId);
        const projectSnap = await tx.get(projectRef);
        const projectData = (projectSnap.data() as any) || {};

        const snap = await tx.get(unitRef);
        const existing = (snap.data() as any) || {};
        const unitNumber = existing.unitNumber || unit.unitNumber || null;
        const activeBookingId: string | null = existing.activeBookingId || null;
        const now = serverTimestamp();
        const by = currentUser.email;

        const bookingRefFor = (id: string) =>
          doc(db, 'projects', currentProjectId, 'units', unit.id, 'bookings', id);

        const ensureProjectWorkflows = async () => {
          const mainId = String(projectData.officeMainWorkflowTemplateId || DEFAULT_OFFICE_MAIN_TEMPLATE_ID);
          const loanId = String(projectData.officeLoanWorkflowTemplateId || DEFAULT_OFFICE_LOAN_TEMPLATE_ID);
          const defaultMainTemplate = defaultOfficeMainTemplate();
          const defaultLoanTemplate = defaultOfficeLoanTemplate();

          const mainRef = doc(db, 'projects', currentProjectId, 'workflowTemplates', mainId);
          const loanRef = doc(db, 'projects', currentProjectId, 'workflowTemplates', loanId);
          // Only seed templates if they don't exist to avoid overwriting customized workflows.
          // Use Settings → Projects → Office Workflow to intentionally reset.
          const mainSnap = await tx.get(mainRef);
          const mainTemplateVersion = Number(
            (mainSnap.data() as any)?.version || defaultMainTemplate.version || 1
          );
          if (!mainSnap.exists()) {
            tx.set(mainRef, {
              ...defaultMainTemplate,
              createdAt: now,
              createdBy: by,
              updatedAt: now,
              updatedBy: by,
            });
          }
          const loanSnap = await tx.get(loanRef);
          if (!loanSnap.exists()) {
            tx.set(loanRef, {
              ...defaultLoanTemplate,
              createdAt: now,
              createdBy: by,
              updatedAt: now,
              updatedBy: by,
            });
          }

          if (!projectData.officeMainWorkflowTemplateId || !projectData.officeLoanWorkflowTemplateId) {
            tx.set(
              projectRef,
              {
                officeMainWorkflowTemplateId: mainId,
                officeLoanWorkflowTemplateId: loanId,
                updatedAt: now,
                updatedBy: by,
              },
              { merge: true }
            );
          }
          return { mainId, loanId, mainTemplateVersion };
        };

        if (next === 'available') {
          if (activeBookingId) {
            const bRef = bookingRefFor(activeBookingId);
            tx.set(
              bRef,
              {
                status: 'cancelled',
                closedAt: now,
                closedBy: by,
                cancelReason: meta?.notes ? String(meta.notes).trim() : null,
                updatedAt: now,
                updatedBy: by,
              },
              { merge: true }
            );

            // Close main + loan cases (if present) for this booking.
            const mainCaseRef = doc(db, 'projects', currentProjectId, 'units', unit.id, 'cases', activeBookingId);
            tx.set(
              mainCaseRef,
              {
                status: 'cancelled',
                closedAt: now,
                closedBy: by,
                closeReason: meta?.notes ? String(meta.notes).trim() : null,
                updatedAt: now,
                updatedBy: by,
              },
              { merge: true }
            );
            const loanCaseRef = doc(db, 'projects', currentProjectId, 'units', unit.id, 'cases', `loan_${activeBookingId}`);
            tx.set(
              loanCaseRef,
              {
                status: 'cancelled',
                closedAt: now,
                closedBy: by,
                closeReason: meta?.notes ? String(meta.notes).trim() : null,
                updatedAt: now,
                updatedBy: by,
              },
              { merge: true }
            );
          }
          tx.set(
            unitRef,
            {
              bookingStatus: 'available',
              activeBookingId: null,
              updatedAt: now,
              updatedBy: by,
            },
            { merge: true }
          );
          return null;
        }

        // booked/blocked/sold
        let bookingId = activeBookingId;
        let newBookingRef: ReturnType<typeof doc> | null = null;
        if (!bookingId) {
          newBookingRef = seededBookingId
            ? doc(db, 'projects', currentProjectId, 'units', unit.id, 'bookings', seededBookingId)
            : doc(collection(unitRef, 'bookings'));
          bookingId = newBookingRef.id;
        }

        const shouldStartMainCase =
          next === 'booked' && Boolean(bookingId);
        const mainIdForCase = String(
          projectData.officeMainWorkflowTemplateId || DEFAULT_OFFICE_MAIN_TEMPLATE_ID
        );
        const mainCaseRef = shouldStartMainCase
          ? doc(
              db,
              'projects',
              currentProjectId,
              'units',
              unit.id,
              'cases',
              String(bookingId)
            )
          : null;
        const existingMainCaseSnap = mainCaseRef ? await tx.get(mainCaseRef) : null;
        let mainTemplateVersion = 1;
        if (shouldStartMainCase) {
          const { mainTemplateVersion: versionFromProject } =
            await ensureProjectWorkflows();
          mainTemplateVersion = versionFromProject;
        }

        if (newBookingRef) {
          tx.set(newBookingRef, {
            status: next === 'sold' ? 'completed' : 'active',
            bookingStatus: next,
            projectId: currentProjectId,
            shortBookingCode: seededShortBookingCode || null,
            unitId: unit.id,
            unitNumber: unitNumber,
            customerName: meta?.customerName ? String(meta.customerName).trim() : null,
            leadId: meta?.leadId ? String(meta.leadId).trim() : null,
            notes: meta?.notes ? String(meta.notes).trim() : null,
            createdAt: now,
            createdBy: by,
            updatedAt: now,
            updatedBy: by,
          });
          if (next === 'sold') {
            tx.set(
              newBookingRef,
              { closedAt: now, closedBy: by },
              { merge: true }
            );
          }
        } else {
          const bRef = bookingRefFor(bookingId);
          const bookingPatch: any = {
            status: next === 'sold' ? 'completed' : 'active',
            bookingStatus: next,
            updatedAt: now,
            updatedBy: by,
          };
          if (meta?.customerName && String(meta.customerName).trim()) bookingPatch.customerName = String(meta.customerName).trim();
          if (meta?.leadId && String(meta.leadId).trim()) bookingPatch.leadId = String(meta.leadId).trim();
          if (meta?.notes && String(meta.notes).trim()) bookingPatch.notes = String(meta.notes).trim();
          tx.set(
            bRef,
            bookingPatch,
            { merge: true }
          );
          if (next === 'sold') {
            tx.set(
              bRef,
              { closedAt: now, closedBy: by },
              { merge: true }
            );
          }
        }

        tx.set(
          unitRef,
          {
            bookingStatus: next,
            activeBookingId: bookingId,
            updatedAt: now,
            updatedBy: by,
          },
          { merge: true }
        );

        // When unit becomes booked from available, auto-start the main workflow case.
        if (shouldStartMainCase && mainCaseRef && !existingMainCaseSnap?.exists()) {
            tx.set(mainCaseRef, {
              caseType: 'main',
              status: 'active',
              bookingId: String(bookingId),
              unitId: unit.id,
              templateId: mainIdForCase,
              templateVersion: mainTemplateVersion,
              loanRequired: null,
              completedStepKeys: [],
              stepData: {},
              startedAt: now,
              startedBy: by,
              updatedAt: now,
              updatedBy: by,
            });
        }
        return bookingId;
      });
      return bookingIdOut || null;
    } finally {
      setSavingUnitId(null);
    }
  };

  const handleChangeStatus = async (unit: UnitRow, next: UnitBookingStatus) => {
    setStatusError(null);
    const current = (unit.bookingStatus || 'available') as UnitBookingStatus;
    if (current === next) return;
    try {
      await updateUnitStatus(unit, next);
      if (next !== 'available') {
        navigate(`/office/units/${unit.id}`);
      }
    } catch (err: any) {
      console.error('Update unit status failed', err);
      setStatusError(err?.message || 'Failed to update unit status.');
    }
  };

  // ── Design tokens ────────────────────────────────────────────────────────
  const GOLD        = '#9d6f32';
  const GOLD_BG     = 'rgba(157,111,50,0.07)';
  const GOLD_BORDER = 'rgba(157,111,50,0.22)';
  const MUTED       = '#6b6b6b';
  const BORDER      = '#e8e8e8';
  const formatRupee = (value: number) => (value > 0 ? `₹${Math.round(value).toLocaleString('en-IN')}` : '');

  // Clickable filter tiles (tiles ARE the filter)
  const filterTiles: { key: typeof filter; label: string; count: number; color: string; bg: string; border: string; activeBg: string }[] = [
    { key: 'booked',    label: 'Booked',    count: counts.booked,    color: GOLD,      bg: GOLD_BG,                   border: GOLD_BORDER,              activeBg: 'rgba(157,111,50,0.18)' },
    { key: 'available', label: 'Available', count: counts.available, color: '#166534', bg: 'rgba(22,101,52,0.07)',    border: 'rgba(22,101,52,0.2)',     activeBg: 'rgba(22,101,52,0.16)' },
    { key: 'form',      label: 'Form',      count: formUnitsCount,   color: '#1565c0', bg: 'rgba(21,101,192,0.06)',   border: 'rgba(21,101,192,0.18)',   activeBg: 'rgba(21,101,192,0.14)' },
    { key: 'all',       label: 'All',       count: units.length,     color: '#1a1a1a', bg: '#fff',                    border: BORDER,                   activeBg: '#f0f0f0' },
  ];
  const activePresetFilters = UNIT_FILTER_OPTIONS.filter((option) => presetFilters.has(option.key));
  const visibleDesktopColumnCount = 2 + Object.values(visibleColumns).filter(Boolean).length;

  if (!currentProjectId) {
    return <Alert severity="warning">Select a project from the header to view units.</Alert>;
  }

  return (
    <Box
      sx={{
        height: 'calc(100vh - var(--office-appbar-height, 56px))',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: '#f9f9f8',
      }}
    >
      {/* ── Header ── */}
      <Box sx={{ px: { xs: 1.5, sm: 3 }, pt: { xs: 1.5, sm: 2.5 }, pb: 1.5, flexShrink: 0 }}>
        {/* Title + search row */}
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ sm: 'center' }} gap={1.5} sx={{ mb: 2 }}>
          <Typography sx={{ fontFamily: "'Georgia','Times New Roman',serif", fontWeight: 700, fontSize: { xs: 20, sm: 24 }, color: '#1a1a1a' }}>
            Units
          </Typography>
          <Stack direction="row" spacing={1} sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <TextField
              size="small"
              placeholder="Search unit, customer, bank…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ minWidth: { xs: 0, sm: 300 }, flex: 1, '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: 2 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchOutlinedIcon fontSize="small" sx={{ color: MUTED }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<ViewColumnOutlinedIcon />}
              onClick={(event) => setColumnsAnchor(event.currentTarget)}
              sx={{ display: { xs: 'none', sm: 'inline-flex' }, flexShrink: 0, borderColor: BORDER, color: MUTED, bgcolor: '#fff' }}
            >
              Columns
            </Button>
          </Stack>
        </Stack>

        <Menu
          anchorEl={columnsAnchor}
          open={Boolean(columnsAnchor)}
          onClose={() => setColumnsAnchor(null)}
          PaperProps={{ sx: { minWidth: 210, borderRadius: 2 } }}
        >
          <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Visible columns
            </Typography>
          </Box>
          {UNIT_COLUMN_OPTIONS.map((option) => (
            <MenuItem key={option.key} dense onClick={() => setVisibleColumns((previous) => ({ ...previous, [option.key]: !previous[option.key] }))}>
              <Checkbox size="small" checked={visibleColumns[option.key]} sx={{ py: 0.25, pr: 1 }} />
              <ListItemText primary={option.label} primaryTypographyProps={{ fontSize: 14 }} />
            </MenuItem>
          ))}
          <Divider />
          <MenuItem dense onClick={() => setVisibleColumns(DEFAULT_VISIBLE_UNIT_COLUMNS)}>
            <ListItemText primary="Show all columns" primaryTypographyProps={{ fontSize: 13, color: GOLD, fontWeight: 700 }} />
          </MenuItem>
        </Menu>

        {/* Clickable filter tiles — tap to filter, active = stronger bg + ring */}
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ overflowX: 'auto', pb: 0.5, '&::-webkit-scrollbar': { height: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: '#d8d8d8', borderRadius: 4 } }}
        >
          {filterTiles.map(({ key, label, count, color, bg, border, activeBg }) => {
            const active = filter === key;
            return (
              <Box
                key={key}
                onClick={() => setFilter(key)}
                sx={{
                  px: 2, py: 0.875,
                  borderRadius: 2,
                  bgcolor: active ? activeBg : bg,
                  border: `1px solid ${active ? color : border}`,
                  minWidth: 72,
                  flexShrink: 0,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                  boxShadow: active ? `0 0 0 2px ${color}22` : 'none',
                  '&:hover': { bgcolor: activeBg },
                }}
              >
                <Typography sx={{ fontWeight: 800, fontSize: 20, color, lineHeight: 1 }}>{count}</Typography>
                <Typography sx={{ fontSize: 11, color: active ? color : MUTED, mt: 0.25, fontWeight: active ? 700 : 500 }}>{label}</Typography>
              </Box>
            );
          })}
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1.25, minWidth: 0 }}>
          <Stack
            direction="row"
            spacing={0.75}
            sx={{ minWidth: 0, overflowX: 'auto', pb: 0.25, '&::-webkit-scrollbar': { height: 4 }, '&::-webkit-scrollbar-thumb': { bgcolor: '#d8d8d8', borderRadius: 4 } }}
          >
            {UNIT_FILTER_OPTIONS.map((option) => {
              const selected = presetFilters.has(option.key);
              return (
                <Chip
                  key={option.key}
                  size="small"
                  label={option.label}
                  onClick={() => togglePresetFilter(option.key)}
                  aria-pressed={selected}
                  sx={{
                    flexShrink: 0,
                    fontWeight: 700,
                    border: `1px solid ${selected ? GOLD : BORDER}`,
                    bgcolor: selected ? GOLD_BG : '#fff',
                    color: selected ? GOLD : MUTED,
                    '&:hover': { bgcolor: selected ? 'rgba(157,111,50,0.14)' : '#f6f6f5' },
                  }}
                />
              );
            })}
          </Stack>
          {activePresetFilters.length ? (
            <Button
              size="small"
              onClick={() => setPresetFilters(new Set())}
              sx={{ flexShrink: 0, minWidth: 0, px: 0.5, color: MUTED, textTransform: 'none', fontSize: 12 }}
            >
              Clear
            </Button>
          ) : null}
        </Stack>
      </Box>

      {/* ── Table ── */}
      <Box sx={{ px: { xs: 0, sm: 3 }, pb: 2, flex: 1, minHeight: 0, overflow: 'auto', overscrollBehavior: 'contain' }}>
        {loadError && <Alert severity="error" sx={{ mb: 2, mx: { xs: 1.5, sm: 0 } }}>{loadError}</Alert>}
        {statusError && <Alert severity="error" sx={{ mb: 2, mx: { xs: 1.5, sm: 0 } }}>{statusError}</Alert>}

        {loading ? (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 4, justifyContent: 'center' }}>
            <CircularProgress size={20} />
            <Typography sx={{ fontSize: 14, color: MUTED }}>Loading units…</Typography>
          </Stack>
        ) : (
          <TableContainer sx={{ borderRadius: { xs: 0, sm: 2 }, border: { sm: `1px solid ${BORDER}` }, overflow: 'hidden' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f9f9f8' }}>
                  <TableCell sx={{ fontWeight: 700, fontSize: 12, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${BORDER}`, pl: { xs: 1.5, sm: 2 } }}>
                    Unit
                  </TableCell>
                  {visibleColumns.customer && (
                    <TableCell sx={{ fontWeight: 700, fontSize: 12, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${BORDER}`, display: { xs: 'none', sm: 'table-cell' } }}>
                      Customer
                    </TableCell>
                  )}
                  {visibleColumns.amount && (
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${BORDER}`, display: { xs: 'none', sm: 'table-cell' } }}>
                      Amount
                    </TableCell>
                  )}
                  {visibleColumns.received && (
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${BORDER}`, display: { xs: 'none', sm: 'table-cell' } }}>
                      Received
                    </TableCell>
                  )}
                  {visibleColumns.pending && (
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${BORDER}`, display: { xs: 'none', sm: 'table-cell' } }}>
                      Pending
                    </TableCell>
                  )}
                  {visibleColumns.loan && (
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${BORDER}`, display: { xs: 'none', sm: 'table-cell' } }}>
                      Loan
                    </TableCell>
                  )}
                  {visibleColumns.gst && (
                    <TableCell sx={{ fontWeight: 700, fontSize: 12, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${BORDER}`, display: { xs: 'none', sm: 'table-cell' } }}>
                      GST
                    </TableCell>
                  )}
                  {visibleColumns.status && (
                    <TableCell sx={{ fontWeight: 700, fontSize: 12, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${BORDER}`, display: { xs: 'none', sm: 'table-cell' } }}>
                      Status
                    </TableCell>
                  )}
                  {visibleColumns.bank && (
                    <TableCell sx={{ fontWeight: 700, fontSize: 12, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${BORDER}`, display: { xs: 'none', sm: 'table-cell' } }}>
                      Loan bank
                    </TableCell>
                  )}
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: 12, color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', borderBottom: `1px solid ${BORDER}`, pr: { xs: 1.5, sm: 2 } }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={isMobile ? 2 : visibleDesktopColumnCount} align="center" sx={{ py: 4, color: MUTED, fontSize: 14 }}>
                      No units found.
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((u) => {
                    const s = (u.bookingStatus || 'available') as UnitBookingStatus;
                    const primaryCustomerName = String(primaryCustomerByUnitId.get(String(u.id || '').trim()) || '').trim();
                    const busy = savingUnitId === u.id;
                    const isBooked = s === 'booked' || s === 'sold';
                    const unitAmount = Math.max(0, Number(u.unitAmount || 0) || 0);
                    const loanAmount = Math.min(unitAmount, Math.max(0, Number(u.loanToDeveloperAmount || 0) || 0));
                    const totals = paymentTotalsByUnitId.get(u.id) || { customer: 0, loan: 0, gst: 0, refunds: 0 };
                    const receivedAmount = Math.max(0, totals.customer + totals.loan - totals.refunds);
                    const pendingAmount = Math.max(0, unitAmount - receivedAmount);
                    const gstAmount = computeGstAmountForUnit(unitAmount, u as any, projectMeta as any, 1);
                    const gstReceived = gstAmount > 0 && totals.gst >= gstAmount;
                    const loanBank = String(sanctionBankByUnitId.get(String(u.id || '').trim()) || '').trim();
                    return (
                      <TableRow
                        key={u.id}
                        hover
                        onClick={() => navigate(`/office/units/${u.id}`)}
                        sx={{
                          cursor: 'pointer',
                          bgcolor: 'transparent',
                          borderLeft: isBooked ? `3px solid ${GOLD}` : '3px solid transparent',
                          '&:hover': { bgcolor: '#f5f5f5' },
                          '& td': { borderBottom: `1px solid #f0f0f0` },
                        }}
                      >
                        {/* Unit number */}
                        <TableCell sx={{ pl: { xs: 1.5, sm: 2 } }}>
                          <Stack spacing={0.25}>
                            <Typography sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1.2, color: isBooked ? GOLD : '#1a1a1a' }}>
                              {u.unitNumber || '—'}
                            </Typography>
                            {isMobile && primaryCustomerName && (
                              <Typography sx={{ fontSize: 12, color: MUTED, lineHeight: 1.2 }}>
                                {primaryCustomerName}
                              </Typography>
                            )}
                            {isMobile && u.blockType && (
                              <Typography sx={{ fontSize: 11, color: MUTED }}>
                                {u.blockType}{u.usage ? ` · ${u.usage}` : ''}
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>

                        {visibleColumns.customer && (
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, color: primaryCustomerName ? '#1a1a1a' : MUTED, fontSize: 13.5 }}>
                            {primaryCustomerName || '—'}
                          </TableCell>
                        )}

                        {visibleColumns.amount && (
                          <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, fontSize: 13.5, color: typeof u.unitAmount === 'number' ? '#1a1a1a' : MUTED }}>
                            {typeof u.unitAmount === 'number'
                              ? formatRupee(unitAmount)
                              : ''}
                          </TableCell>
                        )}

                        {visibleColumns.received && (
                          <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, fontSize: 13.5, color: '#208445', fontWeight: 700 }}>
                            {formatRupee(receivedAmount)}
                          </TableCell>
                        )}

                        {visibleColumns.pending && (
                          <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, fontSize: 13.5, color: pendingAmount > 0 ? '#e05b1a' : '#208445', fontWeight: 700 }}>
                            {formatRupee(pendingAmount)}
                          </TableCell>
                        )}

                        {visibleColumns.loan && (
                          <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' }, fontSize: 13.5, color: loanAmount > 0 ? GOLD : MUTED }}>
                            {formatRupee(loanAmount)}
                          </TableCell>
                        )}

                        {visibleColumns.gst && (
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, color: gstAmount <= 0 ? MUTED : gstReceived ? '#208445' : '#e53935' }}>
                            {gstAmount > 0 && (gstReceived
                              ? <CheckCircleRoundedIcon titleAccess="GST received" sx={{ fontSize: 19, verticalAlign: 'middle' }} />
                              : <CloseRoundedIcon titleAccess="GST pending" sx={{ fontSize: 21, verticalAlign: 'middle' }} />)}
                          </TableCell>
                        )}

                        {visibleColumns.status && (
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                            <Chip size="small" label={statusLabel[s]} color={statusColor[s]} sx={{ fontWeight: 700, height: 24 }} />
                          </TableCell>
                        )}

                        {visibleColumns.bank && (
                          <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, fontSize: 13.5, color: loanBank ? '#1a1a1a' : MUTED }}>
                            {loanBank}
                          </TableCell>
                        )}

                        {/* Action */}
                        <TableCell align="right" sx={{ pr: { xs: 1.5, sm: 2 } }}>
                          {s === 'available' ? (
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); handleChangeStatus(u, 'booked'); }}
                              disabled={busy}
                              title="Mark as booked"
                              aria-label="Mark unit as booked"
                              sx={{ color: '#22c55e', '&:hover': { bgcolor: 'rgba(34,197,94,0.08)' } }}
                            >
                              {busy ? <CircularProgress size={18} /> : <CheckCircleRoundedIcon />}
                            </IconButton>
                          ) : s === 'booked' ? (
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); setConfirmAvailabilityUnit(u); }}
                              disabled={busy}
                              title="Mark as available"
                              aria-label="Mark unit as available"
                              sx={{ color: '#d1d5db', '&:hover': { color: '#ef4444', bgcolor: 'rgba(239,68,68,0.08)' } }}
                            >
                              {busy ? <CircularProgress size={18} /> : <CloseRoundedIcon />}
                            </IconButton>
                          ) : (
                            <Box
                              sx={{
                                display: 'inline-block', px: 1.25, py: 0.25, borderRadius: 999,
                                fontSize: 11, fontWeight: 700,
                                bgcolor: s === 'sold' ? 'rgba(88,28,135,0.08)' : 'rgba(234,179,8,0.1)',
                                color: s === 'sold' ? '#6b21a8' : '#854d0e',
                                border: `1px solid ${s === 'sold' ? 'rgba(88,28,135,0.2)' : 'rgba(234,179,8,0.3)'}`,
                              }}
                            >
                              {statusLabel[s]}
                            </Box>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      <Dialog
        open={Boolean(confirmAvailabilityUnit)}
        onClose={() => setConfirmAvailabilityUnit(null)}
      >
        <DialogTitle>Confirm status change</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Mark unit {confirmAvailabilityUnit?.unitNumber || 'this unit'} as Available?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setConfirmAvailabilityUnit(null)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              if (!confirmAvailabilityUnit) return;
              const nextUnit = confirmAvailabilityUnit;
              setConfirmAvailabilityUnit(null);
              await handleChangeStatus(nextUnit, 'available');
            }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
